import styled from 'styled-components'

import { IconArrowDown } from 'components/Icons/IconArrowDown'
import { IconCheck } from 'components/Icons/IconCheck'
import { Typography } from 'components/Typography/Typography'
import { useCallback, useRef, useState } from 'react'
import { useOnClickOutsideMultiple } from 'utils/onClickOutside'
import { ROUTER_OPTIONS, SwapRouter, useRouter } from 'contexts/SwapRouterContext'
import { Menu } from 'components/Menu/MenuWithPortal'
import { WITHDRAW_DIALOG_ID } from '../WithdrawDialog'

const MenuTrigger = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px;
  border-radius: 8px;
  border: 0.7px solid ${p => p.theme.colors.divider};
  cursor: pointer;
  div {
    display: flex;
    align-items: center;
  }
`

const StyledMenuItem = styled.div`
  & > div {
    padding: 8px 12px;
    display: flex;
    align-items: center;
    cursor: pointer;
    &:hover {
      background-color: ${p => p.theme.colors.background.hover};
    }
  }
`

interface MenuItemProps {
  item: SwapRouter
  activeOption: SwapRouter
  onClick: (o: SwapRouter) => void
}

const getRouterImgUrl = (router: SwapRouter) => {
  switch (router) {
    case SwapRouter.Cetus:
      return '/cetus.png'
    case SwapRouter.Aftermath:
      return '/aftermath.png'
    default:
      return undefined
  }
}

const MenuItem = ({ item, onClick, activeOption }: MenuItemProps) => {
  const imageUrl = getRouterImgUrl(item)

  return (
    <StyledMenuItem onClick={() => onClick(item)}>
      <Typography variant="caption" fontWeight="bold">
        {imageUrl ? <img src={imageUrl} style={{ width: 24, height: 24, marginRight: 8 }} /> : null}
        {item}
        {item === activeOption && (
          <div style={{ marginLeft: 'auto', display: 'flex' }}>
            <IconCheck />
          </div>
        )}
      </Typography>
    </StyledMenuItem>
  )
}

export const SelectSwapAggregatorMenu = () => {
  const { onRouterChange, routerLabel } = useRouter()

  const [isMenuOpen, setMenuOpen] = useState(false)

  const menuTriggerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const onClose = useCallback(() => setMenuOpen(false), [])

  useOnClickOutsideMultiple([menuTriggerRef, menuRef], onClose)

  const toggleMenu = useCallback(() => setMenuOpen(!isMenuOpen), [isMenuOpen])

  const handleOptionClick = useCallback(
    (router: SwapRouter) => {
      onRouterChange(router)
      toggleMenu()
    },
    [onRouterChange, toggleMenu]
  )

  const imageUrl = getRouterImgUrl(routerLabel)

  return (
    <div style={{ position: 'relative', marginTop: 8 }}>
      <MenuTrigger onClick={toggleMenu} ref={menuTriggerRef}>
        <Typography variant="caption" fontWeight="bold">
          {imageUrl ? <img src={imageUrl} style={{ width: 16, height: 16, marginRight: 8 }} /> : null}
          {routerLabel}
        </Typography>
        <div>
          <IconArrowDown />
        </div>
      </MenuTrigger>
      {isMenuOpen && (
        <Menu ref={menuRef} anchorElement={menuTriggerRef} scrollContainerId={WITHDRAW_DIALOG_ID}>
          {ROUTER_OPTIONS.map(o => (
            <MenuItem key={o} item={o} onClick={handleOptionClick} activeOption={routerLabel} />
          ))}
        </Menu>
      )}
    </div>
  )
}
